import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { array, eq, string } from 'fp-ts';
import { debounce, DebounceTimeoutValues, DropdownItem, DropdownItemProps, Stack, StackItem } from '@optimus-web/core';
import { compactArray, keyById } from '@mews/fp';
import { FormSelectField, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { MfaConfiguration, ProfileDataDiscriminator, useRepositoryQuery } from '@mews-commander/data-access';
import { LocalizedTypography } from '@mews-commander/ui';
import { useOwnerIdQueryPayload } from '../utils/useOwnerId';
import { formValues } from './hooks/useMfaSetupForm';

type FormOption = Pick<DropdownItemProps, 'value' | 'label'> & { key: string };

type SelectedProfiles = Record<string, { id: string; email?: string | null }>;

export const Exemptions = ({
    data: { exemptEnterpriseProfiles, enforceAuthenticatorSetup },
}: {
    data: MfaConfiguration;
}) => {
    const [search, setSearch] = useState('');
    const [selectedProfiles, setSelectedProfiles] = useState<SelectedProfiles>(keyById(exemptEnterpriseProfiles));

    const searchParams = {
        searchToken: search,
        portfolioIncluded: false,
    };

    const { data: usersData, isFetching } = useEnterpriseProfilesSearch(searchParams);

    const onSearch = debounce((value: string) => {
        setSearch(value);
    }, DebounceTimeoutValues.textInput);

    const foundProfiles = useMemo(() => {
        if (!usersData?.profiles?.length) {
            return [];
        }

        return compactArray(
            usersData.profiles.map((profile) =>
                profile.data.discriminator === ProfileDataDiscriminator.EnterpriseProfile && profile.isActive
                    ? { email: profile.data.value.email, id: profile.id }
                    : null
            )
        );
    }, [usersData]);

    const { watch } = useFormContext<formValues>();
    const exemptedProfileIdsValue = watch('exemptedEnterpriseProfileIds');

    // saves found and selected profiles to state - we need to keep track of both email and profile ID
    useEffect(() => {
        const newSelectedProfiles = foundProfiles.filter((profile) => exemptedProfileIdsValue.includes(profile.id));

        setSelectedProfiles((prev) => ({
            ...prev,
            ...keyById(newSelectedProfiles),
        }));
    }, [exemptedProfileIdsValue, foundProfiles]);

    // takes existing exemptions and profiles returned from search and removes duplicates based on profile ID
    const profileOptions: FormOption[] = array
        .uniq(eq.contramap<string, { id: string; email?: string | null }>((profile) => profile.id)(string.Eq))([
            ...foundProfiles,
            ...Object.values(selectedProfiles),
        ])
        .map((profile) => ({
            label: profile.email || '',
            value: profile.id,
            key: profile.id,
        }));

    return (
        <Stack vertical spacing="200">
            <StackItem>
                <LocalizedTypography
                    textStyle="bodyMedium"
                    textKey={TextKey.TwoFactorAuthenticationExceptionsDescription}
                    as="div"
                />
            </StackItem>
            <FormSelectField
                label={<LocalizedText textKey={TextKey.EmailAddressPlural} />}
                loader={isFetching}
                multiselect
                compact
                searchPlaceholder={<LocalizedText textKey={TextKey.Search} />}
                emptyMessage={<LocalizedText textKey={TextKey.NoResultsForSearch} />}
                onSearchChange={onSearch}
                helper={<LocalizedText textKey={TextKey.TwoFactorAuthenticationExceptionsHelp} />}
                name="exemptedEnterpriseProfileIds"
                disabled={!enforceAuthenticatorSetup}
            >
                {profileOptions.map((option) => (
                    <DropdownItem {...option} />
                ))}
            </FormSelectField>
        </Stack>
    );
};

const useEnterpriseProfilesSearch = ({
    searchToken,
    portfolioIncluded = true,
}: {
    searchToken: string;
    portfolioIncluded?: boolean;
}) => {
    const ownerIdPayload = useOwnerIdQueryPayload();
    return useRepositoryQuery('enterpriseProfiles', 'search', {
        searchToken,
        count: 10, // todo: add infinite loading
        portfolioIncluded,
        ...ownerIdPayload,
    });
};
